import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  BarChart3,
  Brain,
  KeyRound,
  LoaderCircle,
  Mail,
  RefreshCw,
  Search,
  Sparkles,
  Workflow,
  X,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AUTH_STATUSES, useAuth } from "../contexts/AuthContext";
import { AuthCard, AuthNotice } from "../components/AuthComponents";
import ThemeToggle from "../components/ThemeToggle";

const features = [
  {
    icon: Search,
    title: "Signal Intelligence",
    description: "Discover startups before they become obvious across web, talent, news, and product trails.",
  },
  {
    icon: Brain,
    title: "AI Diligence",
    description: "Generate memos, pressure-test conviction, and interrogate company materials without a spreadsheet slog.",
  },
  {
    icon: BarChart3,
    title: "Decision Velocity",
    description: "Score, shortlist, and keep your private pipeline moving from raw signal to action in one system.",
  },
  {
    icon: Mail,
    title: "Outreach Loop",
    description: "Move from thesis to warm contact with context-rich drafts and a cleaner relationship workflow.",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const {
    loading,
    error,
    authStatus,
    isAuthenticated,
    localAuthEnabled,
    localAuthProfile,
    loginWithLocalBypass,
    loginWithPasscode,
    checkAuth,
    clearError,
  } = useAuth();

  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState(localAuthProfile.name);
  const [email, setEmail] = useState(localAuthProfile.email);
  const [passcode, setPasscode] = useState("");

  useEffect(() => {
    setName(localAuthProfile.name);
    setEmail(localAuthProfile.email);
  }, [localAuthProfile.email, localAuthProfile.name]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const openModal = () => {
    setName(localAuthProfile.name);
    setEmail(localAuthProfile.email);
    setPasscode("");
    clearError();
    setShowModal(true);
  };

  const closeModal = () => {
    clearError();
    setShowModal(false);
  };

  const handleLocalAccess = async (event) => {
    event.preventDefault();
    clearError();

    const result = await loginWithLocalBypass({
      name: name.trim() || localAuthProfile.name,
      email: email.trim() || localAuthProfile.email,
    });

    if (result?.ok) {
      setShowModal(false);
      navigate("/dashboard", { replace: true });
    }
  };

  const handlePasscodeAccess = async (event) => {
    event.preventDefault();
    clearError();

    const result = await loginWithPasscode(passcode.trim());

    if (result?.ok) {
      setShowModal(false);
      navigate("/dashboard", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      {showModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 backdrop-blur-md"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeModal();
            }
          }}
        >
          <div className="relative w-full max-w-sm">
            <button
              type="button"
              onClick={closeModal}
              className="absolute right-4 top-4 z-10 rounded-full p-1 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
            >
              <X className="h-4 w-4" />
            </button>

            {loading ? (
              <AuthCard
                icon={LoaderCircle}
                iconClassName="animate-spin"
                className="max-w-none"
                eyebrow="Secure access"
                title="Checking access"
                description="Looking for an active backend session before we open the workspace."
              >
                <AuthNotice tone="neutral">This check happens against the backend session cookie, not localStorage.</AuthNotice>
              </AuthCard>
            ) : (
              <AuthCard
                icon={localAuthEnabled ? Workflow : KeyRound}
                className="max-w-none"
                eyebrow={localAuthEnabled ? "Local development access" : "Invite-only access"}
                title={localAuthEnabled ? "Enter the local workspace" : "Enter your access code"}
                description={
                  localAuthEnabled
                    ? "This build can create a backend-backed local session for development. No passcode ships in the frontend."
                    : "Use your entry code below or open the secure access page if you already have a session-enabled invite link."
                }
              >
                <div className="space-y-4">
                  {error ? <AuthNotice tone="danger">{error}</AuthNotice> : null}

                  {authStatus === AUTH_STATUSES.UNAVAILABLE ? (
                    <>
                      <AuthNotice tone="danger">
                        The authentication service is unavailable right now, so the access gate is staying closed on
                        purpose.
                      </AuthNotice>
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <button
                          type="button"
                          onClick={() => {
                            clearError();
                            void checkAuth();
                          }}
                          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
                        >
                          <RefreshCw className="h-4 w-4" />
                          Retry session check
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate("/login")}
                          className="flex-1 rounded-xl border border-zinc-700 px-4 py-3 text-sm font-medium text-zinc-200 transition-colors hover:border-zinc-500 hover:bg-zinc-800"
                        >
                          Open login page
                        </button>
                      </div>
                    </>
                  ) : localAuthEnabled ? (
                    <form onSubmit={handleLocalAccess} className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono">Display name</label>
                        <input
                          type="text"
                          value={name}
                          autoComplete="name"
                          onChange={(event) => {
                            setName(event.target.value);
                            clearError();
                          }}
                          placeholder="Local Demo User"
                          className="w-full rounded-xl border border-zinc-700 bg-zinc-800 px-4 py-3 text-sm text-zinc-50 placeholder:text-zinc-500 focus:border-zinc-500 focus:outline-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono">Email</label>
                        <input
                          type="email"
                          value={email}
                          autoComplete="email"
                          onChange={(event) => {
                            setEmail(event.target.value);
                            clearError();
                          }}
                          placeholder="local-demo@venturecopilot.dev"
                          className="w-full rounded-xl border border-zinc-700 bg-zinc-800 px-4 py-3 text-sm text-zinc-50 placeholder:text-zinc-500 focus:border-zinc-500 focus:outline-none"
                        />
                      </div>

                      <AuthNotice tone="neutral">
                        Dev-only bypass. Frontend requires `REACT_APP_ENABLE_LOCAL_AUTH_BYPASS=true`; backend requires
                        `LOCAL_AUTH_BYPASS=true`.
                      </AuthNotice>

                      <button
                        type="submit"
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
                      >
                        Continue to dashboard
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handlePasscodeAccess} className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono">Entry code</label>
                        <input
                          type="password"
                          value={passcode}
                          autoComplete="one-time-code"
                          onChange={(event) => {
                            setPasscode(event.target.value);
                            clearError();
                          }}
                          placeholder="Enter your access code"
                          className="w-full rounded-xl border border-zinc-700 bg-zinc-800 px-4 py-3 text-sm tracking-[0.08em] text-zinc-50 placeholder:text-zinc-500 focus:border-zinc-500 focus:outline-none"
                        />
                      </div>

                      <AuthNotice tone="neutral">
                        Entry codes create secure backend sessions. Invite/session links still work if you already have
                        one.
                      </AuthNotice>
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <button
                          type="submit"
                          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-white px-4 py-3 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
                        >
                          Continue to dashboard
                          <ArrowRight className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate("/login")}
                          className="flex-1 rounded-xl border border-zinc-700 px-4 py-3 text-sm font-medium text-zinc-200 transition-colors hover:border-zinc-500 hover:bg-zinc-800"
                        >
                          Open secure access
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </AuthCard>
            )}
          </div>
        </div>
      ) : null}

      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.16),transparent_28%),radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.09),transparent_22%),linear-gradient(135deg,rgba(255,255,255,0.03),transparent_55%)]" />
        <div className="pointer-events-none absolute inset-0 opacity-60 [background-image:radial-gradient(circle_at_center,rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:18px_18px] [mask-image:linear-gradient(to_bottom,black,transparent)]" />

        <div className="relative z-10 mx-auto max-w-6xl px-6 pb-24 pt-10">
          <nav className="mb-20 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white text-zinc-900 shadow-sm">
                <Zap className="h-5 w-5" />
              </div>
              <span className="text-xl font-medium tracking-tight font-['Outfit']">Venture Copilot</span>
            </div>

            <div className="flex items-center gap-3">
              <ThemeToggle />
              <button
                type="button"
                onClick={openModal}
                className="rounded-xl bg-white px-5 py-2.5 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
              >
                Enter now
              </button>
            </div>
          </nav>

          <div className="grid items-end gap-14 lg:grid-cols-[minmax(0,1.1fr)_380px]">
            <div className="max-w-3xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.24em] text-zinc-400">
                <Sparkles className="h-3.5 w-3.5" />
                Invite-only investor system
              </div>
              <h1 className="max-w-3xl text-5xl font-light leading-[0.98] tracking-tight text-zinc-50 sm:text-6xl lg:text-7xl font-['Outfit']">
                Your autonomous
                <br />
                <span className="text-zinc-400">venture analyst</span>
              </h1>
              <p className="mt-8 max-w-2xl text-lg leading-relaxed text-zinc-400 font-['Manrope']">
                The intelligence terminal for ambitious solo investors. Source startups, build conviction, and execute
                outreach faster than a traditional analyst stack.
              </p>
              <div className="mt-10 flex items-center gap-4">
                <button
                  type="button"
                  onClick={openModal}
                  className="flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-base font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
                >
                  Enter now
                  <ArrowRight className="h-4 w-4" />
                </button>
                <span className="text-sm uppercase tracking-[0.26em] text-zinc-500 font-mono">Backend-gated access</span>
              </div>
            </div>

            <div className="rounded-[28px] border border-white/10 bg-white/5 p-5 shadow-[0_20px_80px_rgba(0,0,0,0.28)] backdrop-blur-xl">
              <div className="mb-4 flex items-center justify-between border-b border-white/10 pb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-zinc-500 font-mono">Live snapshot</p>
                  <h2 className="mt-2 text-xl text-zinc-50 font-['Outfit']">Operator cockpit</h2>
                </div>
                <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-xs text-emerald-300 font-mono">
                  ready
                </div>
              </div>
              <div className="space-y-3">
                {[
                  "New founder signal clusters this week",
                  "Under-the-radar companies with deck activity",
                  "Warm paths into thesis-aligned investors",
                ].map((item, index) => (
                  <div key={item} className="rounded-2xl border border-white/8 bg-black/20 p-4">
                    <div className="mb-2 text-xs uppercase tracking-[0.24em] text-zinc-500 font-mono">0{index + 1}</div>
                    <p className="text-sm leading-relaxed text-zinc-300 font-['Manrope']">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="mx-auto max-w-6xl px-6 py-10">
        <div className="grid gap-6 md:grid-cols-2">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="rounded-3xl border border-zinc-800 bg-zinc-900/70 p-6 shadow-[0_1px_0_rgba(255,255,255,0.03)] backdrop-blur"
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-800 text-zinc-300">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-xl text-zinc-50 font-['Outfit']">{feature.title}</h3>
                <p className="mt-3 leading-relaxed text-zinc-400 font-['Manrope']">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      <footer className="mx-auto flex max-w-6xl items-center justify-between border-t border-zinc-800 px-6 py-8">
        <div className="flex items-center gap-2 text-zinc-500">
          <Zap className="h-4 w-4" />
          <span className="text-sm font-mono">Venture Copilot</span>
        </div>
        <span className="text-sm text-zinc-600 font-mono">2026</span>
      </footer>
    </div>
  );
}
